import { AfterViewInit, ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { ButtonComponent } from '../../../buttons/button/button.component';
import { CheckboxComponent } from '../../../interactive-labels/checkbox/checkbox.component';
import { TableFilter, TableFilterOption } from '../../models/table-filter';
import { ButtonType } from '../../../enum/button-type.enum';
import { TextInputComponent } from '../../../form/inputs/text-input/text-input.component';
import { InputSize } from '../../../enum/input-size.enum';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'lib-numeric-filter',
  imports: [ButtonComponent, CheckboxComponent, TextInputComponent, ReactiveFormsModule],
  templateUrl: './numeric-filter.component.html',
  styleUrl: './numeric-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumericFilterComponent implements AfterViewInit {
  tableFilter = input.required<TableFilter>();

  protected readonly ButtonType = ButtonType;
  protected readonly InputSize = InputSize;

  protected useRange = signal<boolean>(false);
  protected exactValueFormControlId = 'exactValue';
  protected minValueFormControlId = 'minValue';
  protected maxValueFormControlId = 'maxValue';
  protected exactValueFormControl = new FormControl<string | null>(null);
  protected minValueFormControl = new FormControl<string | null>(null);
  protected maxValueFormControl = new FormControl<string | null>(null);

  private tableFilterListener = effect(() => {
    const options = this.tableFilter().unsavedOptions ?? [];
    const exactValueOption = options.find(o => o.isExactValue);
    const minValueOption = options.find(o => o.isMinValue);
    const maxValueOption = options.find(o => o.isMaxValue);
    if (exactValueOption) {
      this.exactValueFormControl.setValue(`${exactValueOption.value}`);
    }
    if (minValueOption) {
      this.minValueFormControl.setValue(`${minValueOption.value}`);
    }
    if (maxValueOption) {
      this.maxValueFormControl.setValue(`${maxValueOption.value}`);
    }

    if (minValueOption?.value || maxValueOption?.value) {
      this.useRange.set(true);
    }
  });

  private exactValueListener = this.exactValueFormControl.valueChanges.subscribe(v => {
    const value = this.getNumberValue(v);
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isExactValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = value;
      existingTableFilterOption.selected = value !== null;
    } else {
      const tableFilterOption = {
        name: this.exactValueFormControlId,
        selected: value !== null,
        value: value,
        isExactValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  private minValueListener = this.minValueFormControl.valueChanges.subscribe(v => {
    const value = this.getNumberValue(v);
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isMinValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = value;
      existingTableFilterOption.selected = value !== null;
    } else {
      const tableFilterOption = {
        name: this.minValueFormControlId,
        selected: value !== null,
        value: value,
        isMinValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  private maxValueListener = this.maxValueFormControl.valueChanges.subscribe(v => {
    const value = this.getNumberValue(v);
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isMaxValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = value;
      existingTableFilterOption.selected = value !== null;
    } else {
      const tableFilterOption = {
        name: this.maxValueFormControlId,
        selected: value !== null,
        value: value,
        isMaxValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  ngAfterViewInit(): void {
    this.tableFilter()?.resetAll$.subscribe(() => {
      this.resetFormControls();
      this.useRange.set(false);
    });
  }

  rangeCheckboxClicked() {
    this.resetFormControls();
    this.useRange.set(!this.useRange());
  }

  resetFormControls() {
    this.exactValueFormControl.reset();
    this.minValueFormControl.reset();
    this.maxValueFormControl.reset();
  }

  resetClicked() {
    this.tableFilter().resetUnsavedOptions();
  }

  private getNumberValue(value: string | null): number | null {
    if (value === null) {
      return null;
    }
    const parsedValue = parseInt(value, 10);
    return isNaN(parsedValue) ? null : parsedValue;
  }
}
